import { Col, Input, Modal, Row } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import VSpace from '../../common/VSpace';
import { DatacenterReportTypes, DateRanges, getDatacenterReportTypeNames, getFiltersFromJson, ReportFilterViewModel, updateFilterDates } from '../datacenterModel';
import { useCreateFiltersMutation, useDeleteFiltersMutation, useGetFiltersQuery } from '../datacenterQueries';
import ReportFilters from '../ReportFilters';
import ReportSavedFiltersList from '../ReportSavedFiltersList';
import ReportStatistics from '../ReportStatistics';
import SaveFilterModal from '../SaveFilterModal';
import { InvoiceReportData } from './invoiceReportApi';
import InvoiceReportInvoicesList from './InvoiceReportInvoicesList';
import { getFilteredItems, getInvoiceStatistics } from './invoiceReportModel';
import { useGetInvoiceReportDataQuery } from './invoiceReportQueries';
import InvoiceReportFilterDetails from './InvoiceReportFilterDetails';
import { projectTypeOptionsFor } from '../../../common/constants';
import useServiceTicketsEnabled from '../../../hooks/useServiceTicketsEnabled';

export type InvoiceReportFilters = {
  startDate: Dayjs;
  endDate: Dayjs;
  searchTerm: string;
  dateRange: DateRanges;
  jobTypes: number[]
}

const InvoiceReport = () => {
  const showServiceTickets = useServiceTicketsEnabled()
  const projectTypeOptions = projectTypeOptionsFor(showServiceTickets)
  const today = dayjs();
  const defaultStartDate = today.subtract(6, 'month');
  const defaultFilters: InvoiceReportFilters = {
    startDate: defaultStartDate,
    endDate: today,
    searchTerm: '',
    dateRange: 'custom',
    jobTypes: []
  }

  const [open, setOpen] = useState(false);
  const [deleteFilterModalConfirm, deleteFiltersModalContext] = Modal.useModal();
  const [filterName, setFilterName] = useState('');
  const [filters, setFilters] = useState<InvoiceReportFilters>(defaultFilters);
  const { startDate, endDate } = filters;

  const getInvoiceReportDataQuery = useGetInvoiceReportDataQuery(
    startDate.toISOString().split('T')[0],
    endDate.toISOString().split('T')[0]
  );
  const getFiltersQuery = useGetFiltersQuery(DatacenterReportTypes.InvoiceVolume)
  const createFiltersMutation = useCreateFiltersMutation()
  const deleteFiltersMutation = useDeleteFiltersMutation()

  const filtersList: ReportFilterViewModel[] = getFiltersQuery.data?.map((x) => {
    return {
      ...x,
      filters: getFiltersFromJson(x.filters)
    }
  }).sort((a, b) => a.order - b.order) || [];

  const jobTypesSet = new Set(filters.jobTypes)
  const filteredInvoicesData: InvoiceReportData[] = getFilteredItems(getInvoiceReportDataQuery.data || [], filters.searchTerm.toLowerCase(), jobTypesSet)

  const onSaveFilter = () => {
    createFiltersMutation.mutate({
      request: {
        reportId: DatacenterReportTypes.InvoiceVolume,
        name: filterName,
        reportTypeName: getDatacenterReportTypeNames(DatacenterReportTypes.InvoiceVolume),
        filters: updateFilterDates(filters)
      },
      reportType: DatacenterReportTypes.InvoiceVolume
    });
    setOpen(false);
    setFilterName('');
  }

  return (<Row gutter={16}>
    {
      (filtersList.length > 0) && <Col span={4}>
        <ReportSavedFiltersList
          filtersList={filtersList}
          onFilterSelect={(filters) => setFilters(filters as InvoiceReportFilters)}
          onDeleteFilters={(selectedFiltersIds) => {
            deleteFilterModalConfirm.confirm({
              title: 'Do you want to delete the selected filters?',
              content: 'This action cannot be undone',
              onOk: () => {
                deleteFiltersMutation.mutate({ filtersIds: selectedFiltersIds, reportType: DatacenterReportTypes.InvoiceVolume })
              },
              okText: 'Delete',
              okButtonProps: {
                loading: deleteFiltersMutation.isLoading,
                danger: true
              }
            })
          }}
          renderFilterDetails={(filterItem) => <InvoiceReportFilterDetails filterItem={filterItem} />}
          reportType={DatacenterReportTypes.InvoiceVolume}
        />
      </Col>
    }

    <Col span={filtersList.length > 0 ? 20 : 24}>
      <VSpace>
        <Input.Search placeholder="Filter by job name or customer name" value={filters.searchTerm} onChange={e => setFilters({ ...filters, searchTerm: e.target.value })} />
        <ReportFilters
          dateRangeTitle='Sold Date'
          filters={filters}
          onFiltersChange={setFilters}
          isDataLoading={getInvoiceReportDataQuery.isLoading}
          defaultFilters={defaultFilters}
          onSaveFilters={() => {
            setOpen(true)
          }}
          selectOptions={{
            jobTypes: {
              mode: 'multiple',
              options: projectTypeOptions,
              placeholder: 'Select Job Types',
              style: { width: 300 },
              allowClear: true,
            }
          }}
        />
        <ReportStatistics title='Summary' statistics={getInvoiceStatistics(filteredInvoicesData)} columnWidth={6} />
        <InvoiceReportInvoicesList invoices={filteredInvoicesData} dateRange={{ start: startDate, end: endDate }} />
      </VSpace>
    </Col>

    <SaveFilterModal
      inputValue={filterName}
      onCancel={() => {
        setFilterName('');
        setOpen(false);
      }}
      onInputChange={setFilterName}
      onOk={onSaveFilter}
      open={open}
      title='Save the Filter'
      okText='Save Filter'
      cancelText='Cancel'
    />
    {deleteFiltersModalContext}
  </Row>
  )
}

export default InvoiceReport